/** Detect wide HDL LOGICAL_LINES library SQL (dat-line detail recipe) for preview guard bypass. */
export function sqlLooksLikeHdlLogicalLinesLibrary(sql) {
  if (!sql || typeof sql !== 'string') return false;
  const stripped = sql.replace(/--[^\n]*/g, ' ').replace(/\/\*[\s\S]*?\*\//g, ' ');
  if (!/\bHRC_DL_LOGICAL_LINES\b/i.test(stripped)) return false;
  if (!/\bFROM\s+HRC_DL_LOGICAL_LINES\b/i.test(stripped)) return false;
  const sel = stripped.match(/\bSELECT\b([\s\S]*?)\bFROM\b/i);
  if (!sel) return false;
  let depth = 0;
  let items = 1;
  for (const ch of sel[1]) {
    if (ch === '(') depth += 1;
    else if (ch === ')') depth = Math.max(0, depth - 1);
    else if (ch === ',' && depth === 0) items += 1;
  }
  return items > 12;
}

/** Alias for dat-line library detection (LOGICAL_LINES anchor). */
export const sqlLooksLikeHdlDatLineLibrary = sqlLooksLikeHdlLogicalLinesLibrary;

/** Detect pod-tested active employee roster library SQL (wide SELECT by design). */
export function sqlLooksLikeActiveEmployeeRosterLibrary(sql) {
  if (!sql || typeof sql !== 'string') return false;
  const header = sql.slice(0, 1200);
  if (/active-employee-roster/i.test(header) || /Active Employee Roster/i.test(header)) {
    return true;
  }
  const stripped = sql.replace(/--[^\n]*/g, ' ').replace(/\/\*[\s\S]*?\*\//g, ' ');
  const up = stripped.toUpperCase();
  if (!/\bPER_ALL_PEOPLE_F\b/.test(up) || !/\bPER_ALL_ASSIGNMENTS_M\b/.test(up)) return false;
  if (!/\bPRIMARY_FLAG\s*=\s*'Y'/.test(up) || !/\bEFFECTIVE_LATEST_CHANGE\s*=\s*'Y'/.test(up)) {
    return false;
  }
  const sel = stripped.match(/\bSELECT\b([\s\S]*?)\bFROM\b/i);
  if (!sel) return false;
  let depth = 0;
  let items = 1;
  for (const ch of sel[1]) {
    if (ch === '(') depth += 1;
    else if (ch === ')') depth = Math.max(0, depth - 1);
    else if (ch === ',' && depth === 0) items += 1;
  }
  return items > 12 && /\bPERSON_NUMBER\b/.test(up) && /\bDEPARTMENT\b/.test(up);
}

/** Detect wide HDL MESSAGE_LINES library SQL (detail recipe) for preview guard bypass. */
export function sqlLooksLikeHdlMessageLinesLibrary(sql) {
  if (!sql || typeof sql !== 'string') return false;
  const stripped = sql.replace(/--[^\n]*/g, ' ').replace(/\/\*[\s\S]*?\*\//g, ' ');
  if (!/\bHRC_DL_MESSAGE_LINES\b/i.test(stripped)) return false;
  const sel = stripped.match(/\bSELECT\b([\s\S]*?)\bFROM\b/i);
  if (!sel) return false;
  let depth = 0;
  let items = 1;
  for (const ch of sel[1]) {
    if (ch === '(') depth += 1;
    else if (ch === ')') depth = Math.max(0, depth - 1);
    else if (ch === ',' && depth === 0) items += 1;
  }
  return items > 12;
}

/** Detect pod-tested pending-worker library SQL (slim or full BIP variant). */
export function sqlLooksLikePendingWorkerReportLibrary(sql) {
  if (!sql || typeof sql !== 'string') return false;
  if (/pending-worker-report/i.test(sql.slice(0, 800))) return true;
  const stripped = sql.replace(/--[^\n]*/g, ' ').replace(/\/\*[\s\S]*?\*\//g, ' ');
  const up = stripped.toUpperCase();
  return (
    /\bPER_PERSON_SECURED_LIST_V\b/.test(up) &&
    /\bPER_ALL_ASSIGNMENTS_M\b/.test(up) &&
    /\bASSIGNMENT_TYPE\s+IN\s*\(\s*'P'\s*\)/.test(up) &&
    /\bSYSTEM_PERSON_TYPE\s+IN\s*\(\s*'PWK'\s*\)/.test(up)
  );
}

/** Detect pod-tested pending BPM approvals library SQL (WFTASK + history UNION). */
export function sqlLooksLikePendingBpmApprovalsLibrary(sql) {
  if (!sql || typeof sql !== 'string') return false;
  if (/pending-bpm-approvals/i.test(sql.slice(0, 800))) return true;
  const stripped = sql.replace(/--[^\n]*/g, ' ').replace(/\/\*[\s\S]*?\*\//g, ' ');
  const up = stripped.toUpperCase();
  return (
    /\bWFTASK\b/.test(up) &&
    /\bWFTASKHISTORY\b/.test(up) &&
    /\bUNION\b/.test(up) &&
    /\bFND_CONSOLE_TRANSACTION_INFO\b/.test(up)
  );
}

function questionMentionsTimecard(question) {
  const q = String(question || '').toLowerCase();
  return /\bhxt_/.test(q) || /\btime\s*card\b/.test(q);
}

const WIDE_LIBRARY_RECIPE_IDS = new Set([
  'active-employee-roster',
  'pending-worker-report',
  'pending-worker-report-bip',
  'pending-bpm-approvals',
  'hcm-hdl-load-errors-detail',
  'hcm-hdl-dat-line-errors',
  'hcm-hdl-dat-line-errors-summary',
]);

export function deriveOracleRunHdlFlags({ sql, question, composed, recipeId }) {
  const joinRecipeId =
    recipeId ||
    composed?.queryIntent?.primaryJoinRecipeId ||
    (composed?.patternIds || []).find((id) => WIDE_LIBRARY_RECIPE_IDS.has(id)) ||
    (composed?.patternIds || []).find((id) => /^hcm-hdl-/.test(id)) ||
    (composed?.queryIntent?.domain === 'timecard' ? 'hxt-timecard-matrix' : '') ||
    (questionMentionsTimecard(question) ? 'hxt-timecard-matrix' : '') ||
    (/\bHXT_TM_MTRX\b/i.test(String(sql || '')) ? 'hxt-timecard-matrix' : '') ||
    (sqlLooksLikeActiveEmployeeRosterLibrary(sql) ? 'active-employee-roster' : '') ||
    (sqlLooksLikePendingWorkerReportLibrary(sql) ? 'pending-worker-report' : '') ||
    '';
  const allowFullLibraryRecipe =
    WIDE_LIBRARY_RECIPE_IDS.has(joinRecipeId) ||
    sqlLooksLikeHdlMessageLinesLibrary(sql) ||
    sqlLooksLikeHdlDatLineLibrary(sql) ||
    sqlLooksLikeActiveEmployeeRosterLibrary(sql) ||
    sqlLooksLikePendingWorkerReportLibrary(sql) ||
    sqlLooksLikePendingBpmApprovalsLibrary(sql);
  return {
    joinRecipeId: joinRecipeId || undefined,
    allowFullHdlRecipe: allowFullLibraryRecipe,
    allowFullLibraryRecipe,
    question: question?.trim() || undefined,
  };
}
